import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, Filter, Search, X } from 'lucide-react';
import { Checkbox } from './FormCheck';

/**
 * The portal's one table.
 *
 * Every section screen had hand-written `<thead>/<tbody>` markup, so pagination existed on
 * three pages and nowhere else, there was no filtering, and a wide table on a phone was a
 * horizontal scrollbar. This carries all of that once: sorting, per-column filters the user
 * can switch on and off, column hiding, pagination, and a narrow-screen layout that stacks
 * each row into a labelled card instead of scrolling sideways.
 *
 * Cells stay arbitrary JSX — `render` returns whatever the page wants (badges, buttons,
 * checkboxes). `value` is the separate plain scalar the table sorts and filters on, which
 * is also what the responsive card and the CSV-ish copy paths read. A column with no
 * `value` (an actions column) is simply not sortable or filterable.
 */
export interface DataTableColumn<T> {
  key: string;
  header: string;
  /**
   * Plain scalar for sorting, filtering and the narrow-screen label. Omit for columns that
   * hold only controls — they render, but take no part in sorting or filtering.
   */
  value?: (row: T) => string | number | boolean | null | undefined;
  /** Rich cell. Defaults to the `value` result. */
  render?: (row: T) => ReactNode;
  /**
   * 'text' matches a substring; 'select' offers the column's distinct values. Defaults to
   * 'text' when `value` is present. `false` opts the column out of filtering while leaving
   * it sortable.
   */
  filter?: 'text' | 'select' | false;
  /** Defaults to true when `value` is present. */
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /** Hidden by default; the user can switch it on from the Columns menu. */
  hiddenByDefault?: boolean;
}

/** Supplied when the endpoint already pages server-side, so the footer drives the fetch. */
export interface DataTableServerPagination {
  page: number;
  totalPages: number;
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
  /** What one row is called, for the footer count ("42 users"). */
  noun?: string;
}

export interface DataTableProps<T> {
  /** `null` is what useApiResource yields before the first response lands. */
  rows: T[] | null | undefined;
  columns: DataTableColumn<T>[];
  getRowKey: (row: T, index: number) => string;
  loading?: boolean;
  error?: string | null;
  /** Shown when there are no rows at all. */
  empty?: string;
  /**
   * Stable id for persisting this table's column and filter preferences. Omit and the
   * table still works; the choices just reset on reload.
   */
  id?: string;
  /** Client-side rows per page. `null` renders every row with no pager. */
  pageSize?: number | null;
  /** Hands paging to the caller. Client-side paging is skipped when this is present. */
  pagination?: DataTableServerPagination;
  /** Page-specific controls, rendered at the left of the toolbar. */
  toolbar?: ReactNode;
  rowClassName?: (row: T) => string | undefined;
  /** Row noun for the footer count. */
  noun?: string;
  /**
   * Drops the search / filter / columns toolbar. For the config and matrix tables — pool
   * score weights, the role-permission grid — where the row set is fixed and tiny, so
   * searching or paging it is furniture rather than help. Those tables still come through
   * here for the shared styling and the narrow-screen card layout.
   */
  controls?: boolean;
  /**
   * Full-width companion row rendered directly beneath a row — the drawer pattern several
   * screens use for history timelines and score breakdowns. Return null for rows that
   * aren't expanded; the caller owns which one is open.
   */
  expandedRow?: (row: T) => ReactNode;
}

const PAGE_SIZES = [10, 25, 50, 100];

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

const asText = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '' : String(v);

function readPrefs(id: string | undefined) {
  if (!id) return null;
  try {
    const raw = window.localStorage.getItem(`mediclynk.admin.table.${id}`);
    return raw ? (JSON.parse(raw) as { hidden?: string[]; filtersOpen?: boolean }) : null;
  } catch {
    return null;
  }
}

function writePrefs(id: string | undefined, prefs: { hidden: string[]; filtersOpen: boolean }) {
  if (!id) return;
  try {
    window.localStorage.setItem(`mediclynk.admin.table.${id}`, JSON.stringify(prefs));
  } catch {
    /* private browsing — preferences just don't persist */
  }
}

/** Closes a popover on outside click or Escape. */
function useDismissable(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

export function DataTable<T>({
  rows,
  columns,
  getRowKey,
  loading = false,
  error = null,
  empty = 'Nothing to show.',
  id,
  pageSize = 25,
  pagination,
  toolbar,
  rowClassName,
  noun = 'rows',
  controls = true,
  expandedRow,
}: DataTableProps<T>) {
  const prefs = useMemo(() => readPrefs(id), [id]);

  const [hidden, setHidden] = useState<Set<string>>(
    () =>
      new Set(
        prefs?.hidden ?? columns.filter((c) => c.hiddenByDefault).map((c) => c.key),
      ),
  );
  const [filtersOpen, setFiltersOpen] = useState(prefs?.filtersOpen ?? false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState>(null);
  const [clientPage, setClientPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize ?? 25);

  useEffect(() => {
    writePrefs(id, { hidden: [...hidden], filtersOpen });
  }, [id, hidden, filtersOpen]);

  const columnsRef = useDismissable(columnsOpen, () => setColumnsOpen(false));

  const visibleColumns = columns.filter((c) => !hidden.has(c.key));
  const filterableColumns = columns.filter((c) => c.value && c.filter !== false);

  const allRows = useMemo(() => rows ?? [], [rows]);

  /** Distinct values per select-filtered column, for its dropdown. */
  const selectOptions = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const col of filterableColumns) {
      if (col.filter !== 'select' || !col.value) continue;
      out[col.key] = [...new Set(allRows.map((r) => asText(col.value!(r))).filter(Boolean))].sort();
    }
    return out;
  }, [allRows, filterableColumns]);

  const processed = useMemo(() => {
    let out = allRows;

    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((row) =>
        columns.some((c) => c.value && asText(c.value(row)).toLowerCase().includes(q)),
      );
    }

    for (const [key, raw] of Object.entries(filters)) {
      const needle = raw.trim().toLowerCase();
      if (!needle) continue;
      const col = columns.find((c) => c.key === key);
      if (!col?.value) continue;
      out =
        col.filter === 'select'
          ? out.filter((row) => asText(col.value!(row)).toLowerCase() === needle)
          : out.filter((row) => asText(col.value!(row)).toLowerCase().includes(needle));
    }

    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col?.value) {
        // Copy first: Array.prototype.sort mutates, and `rows` belongs to the caller.
        out = [...out].sort((a, b) => {
          const av = col.value!(a);
          const bv = col.value!(b);
          let cmp: number;
          if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
          else cmp = asText(av).localeCompare(asText(bv), undefined, { numeric: true });
          return sort.dir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return out;
  }, [allRows, columns, search, filters, sort]);

  const activeFilterCount =
    Object.values(filters).filter((v) => v.trim()).length + (search.trim() ? 1 : 0);

  // Any narrowing can strand the viewer past the last page.
  useEffect(() => {
    setClientPage(1);
  }, [search, filters, rowsPerPage]);

  const serverPaged = !!pagination;
  const clientPaged = !serverPaged && pageSize !== null;
  const totalClientPages = clientPaged ? Math.max(1, Math.ceil(processed.length / rowsPerPage)) : 1;
  const page = Math.min(clientPage, totalClientPages);
  const pageRows = clientPaged
    ? processed.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : processed;

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.value || col.sortable === false) return;
    setSort((prev) =>
      prev?.key !== col.key
        ? { key: col.key, dir: 'asc' }
        : prev.dir === 'asc'
          ? { key: col.key, dir: 'desc' }
          : null,
    );
  };

  const toggleColumn = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      // Never hide the last column — an empty table has nothing to switch back on from.
      else if (visibleColumns.length > 1) next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({});
    setSearch('');
  };

  const showEmpty = !loading && processed.length === 0;

  return (
    <div className="dt">
      {(controls || toolbar) && (
      <div className="dt-toolbar">
        <div className="dt-toolbar-left">{toolbar}</div>
        {controls && (
        <div className="dt-toolbar-right">
          <div className="dt-search">
            <Search size={14} className="dt-search-icon" />
            <input
              type="search"
              value={search}
              placeholder="Search"
              aria-label="Search this table"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {filterableColumns.length > 0 && (
            <button
              type="button"
              className={filtersOpen ? 'btn dt-btn active' : 'btn dt-btn'}
              aria-pressed={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <Filter size={14} />
              Filters
              {activeFilterCount > 0 && <span className="dt-badge">{activeFilterCount}</span>}
            </button>
          )}

          <div className="dt-menu-anchor" ref={columnsRef}>
            <button
              type="button"
              className={columnsOpen ? 'btn dt-btn active' : 'btn dt-btn'}
              aria-haspopup="true"
              aria-expanded={columnsOpen}
              onClick={() => setColumnsOpen((v) => !v)}
            >
              <Columns3 size={14} />
              Columns
            </button>
            {columnsOpen && (
              <div className="dt-menu" role="menu">
                <div className="dt-menu-title">Show columns</div>
                {columns.map((col) => (
                  <div className="dt-menu-item" key={col.key}>
                    <Checkbox
                      checked={!hidden.has(col.key)}
                      label={col.header}
                      onChange={() => toggleColumn(col.key)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
      )}

      {controls && filtersOpen && filterableColumns.length > 0 && (
        <div className="dt-filters">
          {filterableColumns
            .filter((col) => !hidden.has(col.key))
            .map((col) => (
              <label className="dt-filter" key={col.key}>
                <span className="dt-filter-label">{col.header}</span>
                {col.filter === 'select' ? (
                  <select
                    value={filters[col.key] ?? ''}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, [col.key]: e.target.value }))
                    }
                  >
                    <option value="">All</option>
                    {(selectOptions[col.key] ?? []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={filters[col.key] ?? ''}
                    placeholder="Any"
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, [col.key]: e.target.value }))
                    }
                  />
                )}
              </label>
            ))}
          {activeFilterCount > 0 && (
            <button type="button" className="btn dt-btn dt-clear" onClick={clearFilters}>
              <X size={13} />
              Clear
            </button>
          )}
          {serverPaged && (
            /* Worth saying plainly: the server sends one page at a time, so anything typed
               here narrows the rows already on screen rather than re-querying. */
            <span className="dt-filter-note">Filters apply to the current page</span>
          )}
        </div>
      )}

      <div className="table-wrap">
        {loading && !rows ? (
          <div className="empty-state">Loading…</div>
        ) : error ? (
          <div className="empty-state">{error}</div>
        ) : showEmpty ? (
          <div className="empty-state">
            {allRows.length === 0 ? empty : 'No rows match the current filters.'}
          </div>
        ) : (
          <table className="data-table dt-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => {
                  const sortable = !!col.value && col.sortable !== false;
                  const active = sort?.key === col.key;
                  return (
                    <th
                      key={col.key}
                      style={{ width: col.width, textAlign: col.align }}
                      aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {sortable ? (
                        <button type="button" className="dt-sort" onClick={() => toggleSort(col)}>
                          {col.header}
                          {active ? (
                            sort!.dir === 'asc' ? (
                              <ArrowUp size={12} />
                            ) : (
                              <ArrowDown size={12} />
                            )
                          ) : (
                            <ChevronsUpDown size={12} className="dt-sort-idle" />
                          )}
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, index) => {
                const expanded = expandedRow?.(row);
                return (
                  <Fragment key={getRowKey(row, index)}>
                    <tr className={rowClassName?.(row)}>
                      {visibleColumns.map((col) => (
                        /* data-label is what the narrow-screen card layout prints as each
                           field's name once the cells stop being table cells. */
                        <td key={col.key} data-label={col.header} style={{ textAlign: col.align }}>
                          {col.render ? col.render(row) : asText(col.value?.(row))}
                        </td>
                      ))}
                    </tr>
                    {expanded ? (
                      <tr className="dt-expanded">
                        <td colSpan={visibleColumns.length}>{expanded}</td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {serverPaged ? (
        pagination.totalPages > 1 && (
          <div className="dt-pager">
            <button
              className="btn"
              disabled={!pagination.hasPrev}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="dt-pager-status">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total}{' '}
              {pagination.noun ?? noun})
            </span>
            <button
              className="btn"
              disabled={!pagination.hasNext}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        )
      ) : clientPaged && processed.length > 0 ? (
        <div className="dt-pager">
          <label className="dt-pager-size">
            Rows
            <select
              value={rowsPerPage}
              aria-label="Rows per page"
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <span className="dt-pager-status">
            {(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, processed.length)} of{' '}
            {processed.length} {noun}
            {processed.length !== allRows.length && ` (filtered from ${allRows.length})`}
          </span>
          <div className="dt-pager-nav">
            <button className="btn" disabled={page <= 1} onClick={() => setClientPage(page - 1)}>
              Previous
            </button>
            <button
              className="btn"
              disabled={page >= totalClientPages}
              onClick={() => setClientPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
